import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { StatusEnum } from '@app/infrastructure/models/status';
import { environment } from '@env/environment';
import { ISelectOptions } from '@models/form/select';
import {
  GradeUnitDayListItemDTO,
  IGradeUnitDayDTO,
  IGradeUnitDayListItemDTO,
  IGradeUnitDayRequest,
} from '@models/grade-unit/grade-unit-day';
import { IPagedResults, PagedResults } from '@models/response';
import { FileService } from '../file.service';
import { NotificationService } from '../notification.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class GradeUnitDayService {
  private controllerRoute: string = 'grade-units';
  private url: string = `${environment.apiRoute}`;

  constructor(
    private apiService: ApiService,
    private fileService: FileService,
    private notificationService: NotificationService,
  ) {
    this.url = `${environment.apiRoute}`;
  }

  public getFakeCard(): IGradeUnitDayListItemDTO {
    const day: IGradeUnitDayListItemDTO = new GradeUnitDayListItemDTO();

    day.gradeNumber = 1;
    day.dayTitle = 'My Day';
    day.dayNumber = 1;
    day.dayStartPage = '1';
    day.dayEndPage = '3';
    day.unitNumber = 10;
    day.dayCoverImageId = 'my cover';
    day.dayCoverImageUrl = 'assets/img/G2U3.png';
    day.status = StatusEnum.UNASSIGNED;

    return day;
  }

  public getGradeUnitDayByDayNumber(
    dayNumber: string,
    unitNumber: number,
    gradeNumber: string,
  ): Observable<IGradeUnitDayDTO> {
    const endpoint = `${this.url}/grades/${gradeNumber}/units/${unitNumber}/days/${dayNumber}`;

    return this.apiService.get<IGradeUnitDayDTO>(endpoint);
  }

  // TODO: Delete dummy status...
  private getNineDays(): IGradeUnitDayListItemDTO[] {
    const days: IGradeUnitDayListItemDTO[] = [];
    for (let i = 1; i <= 9; i++) {
      const nday = { ...this.getFakeCard() };
      nday.status = StatusEnum[Object.keys(StatusEnum)[i]];
      days.push(nday);
    }
    return days;
  }

  public getDummyGradeUnitDayListByUnitNumber(
    gradeNumber: string,
    unitNumber: string,
  ): Observable<IPagedResults<IGradeUnitDayListItemDTO>> {
    const endpoint =
      this.url + `/grades/${gradeNumber}/units/${unitNumber}/days`;
    // TODO: This needs to be removed. Not sure if using somewhere else
    const paged: IPagedResults<IGradeUnitDayListItemDTO> =
      new PagedResults<IGradeUnitDayListItemDTO>();
    paged.results = this.getNineDays();
    return of(paged);
    return this.apiService.get<IPagedResults<IGradeUnitDayListItemDTO>>(
      endpoint,
    );
  }

  public getGradeUnitDayListByUnitNumber(
    gradeNumber: string,
    unitNumber: string,
  ): Observable<IPagedResults<IGradeUnitDayListItemDTO>> {
    const endpoint =
      this.url + `/grades/${gradeNumber}/units/${unitNumber}/days`;

    return this.apiService.get<IPagedResults<IGradeUnitDayListItemDTO>>(
      endpoint,
    );
  }

  public getStudentGradeUnitDayList(
    studentId: string,
    gradeLevel: string,
    unitNumber: number,
  ): Observable<IPagedResults<IGradeUnitDayListItemDTO>> {
    const endpoint = `${this.url}/students/${studentId}/grades/${gradeLevel}/units/${unitNumber}/days`;
    return this.apiService.get<IPagedResults<IGradeUnitDayListItemDTO>>(
      endpoint,
    );
  }

  public getGradeUnitDayByUnitNumber(
    gradeNumber: string,
    unitNumber: string,
    dayNumber: string,
  ): Observable<IGradeUnitDayDTO> {
    const endpoint =
      this.url + `/grades/${gradeNumber}/units/${unitNumber}/days/${dayNumber}`;

    return this.apiService.get<IGradeUnitDayDTO>(endpoint);
  }

  public createGradeUnitDay(
    request: IGradeUnitDayRequest,
    gradeNumber: number,
    unitNumber: number,
  ): Observable<IGradeUnitDayDTO> {
    const endpoint = `${this.url}/grades/${gradeNumber}/units/${unitNumber}/days`;
    const payload = this.setFormDataProps(request);
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');

    return this.apiService
      .post<IGradeUnitDayDTO, FormData>(endpoint, payload, { headers })
      .pipe(
        tap(() => {
          const message = `Day created.`;
          return this.notificationService.showSuccess([message]);
        }),
      );
  }

  public jsonUpdateGradeUnitDay(
    id: number,
    request: IGradeUnitDayRequest,
  ): Observable<IGradeUnitDayDTO> {
    const endpoint = `${this.url}/unit-days/${id}`;
    return this.apiService
      .patch<IGradeUnitDayDTO, IGradeUnitDayRequest>(endpoint, request)
      .pipe(
        tap(() => {
          const message = `Day updated.`;
          return this.notificationService.showSuccess([message]);
        }),
      );
  }

  public updateGradeUnitDay(
    request: IGradeUnitDayRequest,
    dayNumber: number,
    unitNumber: number,
    gradeNumber: number,
  ): Observable<IGradeUnitDayDTO> {
    const endpoint = `${this.url}/grades/${gradeNumber}/units/${unitNumber}/days/${dayNumber}`;
    const payload = this.setFormDataProps(request);
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');

    return this.apiService
      .patch<IGradeUnitDayDTO, FormData>(endpoint, payload, { headers })
      .pipe(
        tap(() => {
          const message = `Day updated.`;
          return this.notificationService.showSuccess([message]);
        }),
      );
  }

  public deleteGradeUnitDay(
    grade: string,
    unit: string,
    id: number,
  ): Observable<IGradeUnitDayDTO> {
    const endpoint = `${this.url}/grades/${grade}/units/${unit}/days/${id}`;

    return this.apiService.delete<IGradeUnitDayDTO>(endpoint).pipe(
      tap(() => {
        const message = `Day deleted.`;
        return this.notificationService.showSuccess([message]);
      }),
    );
  }

  /**
   * Convert the request values to `FormData` to be able to add the `FileList` to upload.
   */
  private setFormDataProps(request: IGradeUnitDayRequest): FormData {
    const props: ISelectOptions[] = [
      {
        name: 'dayTitle',
        value: request.dayTitle,
      },
      {
        name: 'dayNumber',
        value: request.dayNumber.toString(),
      },
      {
        name: 'dayStartPage',
        value: request.dayStartPage,
      },
      {
        name: 'isQuiz',
        value: request.isQuiz ? 'true' : 'false',
      },
    ];
    if (request.dayEndPage)
      props.push({
        name: 'dayEndPage',
        value: request.dayEndPage,
      });
    return this.fileService.buildFormData(
      request.dayCoverImage,
      props,
      'dayCoverImage',
    );
  }
}
